/**
 * Ported VERBATIM from `apr_template/src/pages/FrameworksList.tsx`.
 *
 * Only import paths change. The markup, class names, copy, state and logic are the
 * source's — the stylesheet is copied alongside, and the global classes it also uses
 * resolve because the screen renders inside `AprScreen` (`.apr-app`).
 *
 * Do not "tidy" anything here. A difference is a difference from the source.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../compat/api';
import PageHeader from '../compat/PageHeader';
import HelpPanel from '../compat/HelpPanel';
import type { Help } from '../compat/helpTypes';
import './FrameworksList.css';
import { ASPECTS, ASPECT_COLOR, ASPECT_HINT, ASPECT_OWNER, isAspect, type Aspect } from '../aspects';

/* ============================================================================
 * Framework Management — list / management screen.
 * Each framework is a named assembly of dimensions / components / questions.
 * Supports create, edit, clone and (guarded) delete via authFetch.
 * ==========================================================================*/

interface Framework {
  id: string;
  name: string;
  code: string;
  description: string;
  type: string;
  status: string;
  dimension_count: number;
  component_count: number;
  question_count: number;
  /** The distinct aspects this framework's dimensions are answered under. */
  aspects: Aspect[];
  components_with_questions: number;
  apr_model_count: number;
  application_count: number;
}

type Toast = { type: 'success' | 'error'; message: string } | null;

const FRAMEWORK_TYPES = ['Business', 'Utility', 'Infrastructure', 'OT', 'Security'] as const;
const FRAMEWORK_STATUSES = ['Active', 'Draft', 'Archived'] as const;

interface FormState {
  name: string;
  code: string;
  description: string;
  type: string;
  status: string;
}

const emptyForm: FormState = {
  name: '',
  code: '',
  description: '',
  type: 'Business',
  status: 'Active',
};

interface CloneState {
  name: string;
  code: string;
  description: string;
}

// ---------------------------------------------------------------------------
// Contextual help
// ---------------------------------------------------------------------------

const FrameworkManagementHelp: Help = {
  title: 'Framework Management',
  icon: '🧩',
  purpose:
    'A <strong>framework</strong> is a reusable assessment blueprint — an assembly of dimensions, components and questions. Create, clone and refine frameworks here, then design their contents in the Designer.',
  doHere: [
    'Click <strong>Create Framework</strong> to add a new blueprint (name, code, type and status).',
    'Click a framework <strong>name</strong> or <strong>Build</strong> to open it in the Framework Builder and edit its dimensions, components and questions.',
    'Use <strong>Clone</strong> to copy an existing framework as a starting point.',
    'Use <strong>Edit</strong> to rename or re-classify a framework, and <strong>Delete</strong> to remove one that is not in use.',
    'Watch <strong>Completeness</strong> to see how many components already have questions.',
  ],
  fits: 'Application Evaluation &rarr; Framework Design &rarr; Framework Management',
  firstTime:
    'Create a framework, open its Designer, then add dimensions, components and questions until Completeness reaches 100%.',
  sections: [
    {
      type: 'defs',
      label: 'Framework status',
      items: [
        { term: 'In Design', color: '#202124', def: 'Not yet used by any APR model — fully editable.' },
        { term: 'Locked', color: '#9a3412', def: 'Assigned to one or more APR models — structure is protected.' },
      ],
    },
  ],
};

// ---------------------------------------------------------------------------
// Low-level API helper — mirrors the FrameworkBuilder convention.
// Throws Error(serverMessage) on non-2xx so callers can surface it.
// ---------------------------------------------------------------------------

const apiCall = async (url: string, method: string, body?: unknown): Promise<any> => {
  const res = await authFetch(url, {
    method,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (!res.ok) {
    let msg = res.statusText;
    try {
      const data = await res.json();
      msg = data?.error || data?.message || msg;
    } catch {
      // ignore parse failure
    }
    const err = new Error(msg || `Request failed (${res.status})`);
    (err as Error & { status?: number }).status = res.status;
    throw err;
  }
  const text = await res.text();
  return text ? JSON.parse(text) : null;
};

const toNumber = (raw: unknown): number => Number(raw) || 0;

const normalizeFramework = (raw: any): Framework => ({
  id: raw?.id != null ? String(raw.id) : '',
  name: typeof raw?.name === 'string' ? raw.name : '',
  code: typeof raw?.code === 'string' ? raw.code : '',
  description: typeof raw?.description === 'string' ? raw.description : '',
  type: typeof raw?.type === 'string' && raw.type ? raw.type : 'Business',
  status: typeof raw?.status === 'string' && raw.status ? raw.status : 'Draft',
  dimension_count: toNumber(raw?.dimension_count),
  component_count: toNumber(raw?.component_count),
  question_count: toNumber(raw?.question_count),
  // The server sends a comma-joined DISTINCT set; re-ordered here into the canonical
  // ASPECTS order so two frameworks covering the same aspects always look identical.
  aspects: ASPECTS.filter((a) =>
    String(raw?.aspects ?? '')
      .split(',')
      .filter(isAspect)
      .includes(a),
  ),
  components_with_questions: toNumber(raw?.components_with_questions),
  apr_model_count: toNumber(raw?.apr_model_count),
  application_count: toNumber(raw?.application_count),
});

// ---------------------------------------------------------------------------
// Derived helpers
// ---------------------------------------------------------------------------

const isLocked = (fw: Framework): boolean => fw.apr_model_count > 0;

const completeness = (fw: Framework): number =>
  fw.component_count > 0
    ? Math.round((fw.components_with_questions / fw.component_count) * 100)
    : 0;

const summaryLine = (fw: Framework): string => {
  const base = `${fw.dimension_count} dimension${fw.dimension_count === 1 ? '' : 's'}, ${fw.component_count} component${fw.component_count === 1 ? '' : 's'}, ${fw.question_count} question${fw.question_count === 1 ? '' : 's'}`;
  return fw.description.trim() ? `${base} — ${fw.description.trim()}` : base;
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const FrameworksList: React.FC = () => {
  const navigate = useNavigate();

  const [frameworks, setFrameworks] = useState<Framework[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<Toast>(null);

  // Create / edit modal
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Framework | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [saving, setSaving] = useState(false);

  // Clone modal
  const [cloning, setCloning] = useState<Framework | null>(null);
  const [cloneForm, setCloneForm] = useState<CloneState>({ name: '', code: '', description: '' });

  // Delete modal
  const [deleting, setDeleting] = useState<Framework | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [canForceDelete, setCanForceDelete] = useState(false);
  const [deletingBusy, setDeletingBusy] = useState(false);

  // --- data load ----------------------------------------------------------
  const loadFrameworks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiCall('/api/frameworks', 'GET');
      const list: Framework[] = Array.isArray(data) ? data.map(normalizeFramework) : [];
      setFrameworks(list);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load frameworks');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFrameworks();
  }, [loadFrameworks]);

  // auto-dismiss toast
  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 3000);
    return () => window.clearTimeout(t);
  }, [toast]);

  const notifyOk = (message: string) => setToast({ type: 'success', message });
  const notifyErr = (message: string) => setToast({ type: 'error', message });

  /*
   * The PLATFORM route, not the source's.
   *
   * This was `/framework/builder?framework=…`, copied verbatim from APR where that is a real
   * path. Here the shell resolves the first segment as a module, so it looked for a product
   * called "framework" and answered "Module not available — 'framework' is not licensed for
   * this install", which reads as a licensing fault rather than a broken link. Every screen
   * in this module lives under `/apr/…`; see AprModule.tsx:63.
   */
  const openBuilder = (id: string) =>
    navigate(`/apr/settings/framework-builder?framework=${encodeURIComponent(id)}`);

  // --- create / edit ------------------------------------------------------
  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (fw: Framework) => {
    setEditing(fw);
    setForm({
      name: fw.name,
      code: fw.code,
      description: fw.description,
      type: fw.type,
      status: fw.status,
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setForm(emptyForm);
  };

  const handleFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const submitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.code.trim()) {
      notifyErr('Name and code are required');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await apiCall(`/api/frameworks/${editing.id}`, 'PUT', {
          name: form.name.trim(),
          description: form.description.trim(),
          type: form.type,
          status: form.status,
          default_for_application_type: null,
        });
        notifyOk('Framework updated');
      } else {
        await apiCall('/api/frameworks', 'POST', {
          name: form.name.trim(),
          code: form.code.trim(),
          description: form.description.trim(),
          type: form.type,
          status: form.status,
          default_for_application_type: null,
        });
        notifyOk('Framework created');
      }
      closeForm();
      await loadFrameworks();
    } catch (err) {
      notifyErr(err instanceof Error ? err.message : 'Failed to save framework');
    } finally {
      setSaving(false);
    }
  };

  // --- clone --------------------------------------------------------------
  const openClone = (fw: Framework) => {
    setCloning(fw);
    setCloneForm({
      name: `${fw.name} (Copy)`,
      code: `${fw.code}_COPY`,
      description: fw.description,
    });
  };

  const closeClone = () => setCloning(null);

  const handleCloneChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setCloneForm((prev) => ({ ...prev, [name]: value }));
  };

  const submitClone = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!cloning) return;
    if (!cloneForm.name.trim() || !cloneForm.code.trim()) {
      notifyErr('Name and code are required');
      return;
    }
    setSaving(true);
    try {
      await apiCall(`/api/frameworks/${cloning.id}/clone`, 'POST', {
        name: cloneForm.name.trim(),
        code: cloneForm.code.trim(),
        description: cloneForm.description.trim(),
      });
      notifyOk('Framework cloned');
      closeClone();
      await loadFrameworks();
    } catch (err) {
      notifyErr(err instanceof Error ? err.message : 'Failed to clone framework');
    } finally {
      setSaving(false);
    }
  };

  // --- delete -------------------------------------------------------------
  const openDelete = (fw: Framework) => {
    setDeleting(fw);
    setDeleteError(null);
    setCanForceDelete(false);
  };

  const closeDelete = () => {
    setDeleting(null);
    setDeleteError(null);
    setCanForceDelete(false);
  };

  const performDelete = async (force: boolean) => {
    if (!deleting) return;
    setDeletingBusy(true);
    setDeleteError(null);
    try {
      const url = force
        ? `/api/frameworks/${deleting.id}?force=true`
        : `/api/frameworks/${deleting.id}`;
      await apiCall(url, 'DELETE');
      notifyOk('Framework deleted');
      closeDelete();
      await loadFrameworks();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete framework';
      setDeleteError(message);
      // Offer a force delete only when the guard is about assigned models.
      if (!force && /model/i.test(message)) {
        setCanForceDelete(true);
      }
    } finally {
      setDeletingBusy(false);
    }
  };

  // --- render helpers -----------------------------------------------------
  const renderStatusBadge = (fw: Framework) => {
    const locked = isLocked(fw);
    return (
      <span className={`fwl-status ${locked ? 'fwl-status-locked' : 'fwl-status-design'}`}>
        {locked ? 'Locked' : 'In Design'}
      </span>
    );
  };

  const renderCompleteness = (fw: Framework) => {
    const pct = completeness(fw);
    const full = pct >= 100;
    return (
      <div className="fwl-completeness">
        <div className="fwl-bar-track">
          <div
            className="fwl-bar-fill"
            style={{ width: `${pct}%` }}
          />
        </div>
        <span className={`fwl-bar-pct${full ? ' is-full' : ''}`}>{pct}%</span>
      </div>
    );
  };

  const renderActions = (fw: Framework) => {
    const locked = isLocked(fw);
    return (
      <div className="fwl-row-actions">
        {/*
         * NO BUILD ON A LOCKED FRAMEWORK. Owner-directed, 2026-08-10: *"When the framework
         * is in locked status. That means it has been being used, and there is no way that
         * we should build or rebuild that framework."*
         *
         * `isLocked` here is DERIVED, not a column — `apr_model_count > 0`, i.e. the
         * framework is assigned to at least one APR model. The screen already hid Edit and
         * Delete behind it; Build was the one action that ignored it, which is what the
         * owner found.
         *
         * DELIBERATE DEVIATION from the verbatim port (see this file's header). Owner
         * commissioned it, so it is logged in docs/DEVIATIONS.md rather than left as a
         * silent difference from apr_template.
         */}
        {!locked && (
          <button
            type="button"
            className="fwl-act fwl-act-designer"
            onClick={() => openBuilder(fw.id)}
          >
            Build
          </button>
        )}
        {!locked && (
          <button
            type="button"
            className="fwl-act fwl-act-edit"
            onClick={() => openEdit(fw)}
          >
            Edit
          </button>
        )}
        <button
          type="button"
          className="fwl-act fwl-act-clone"
          onClick={() => openClone(fw)}
        >
          Clone
        </button>
        {!locked && (
          <button
            type="button"
            className="fwl-act fwl-act-delete"
            onClick={() => openDelete(fw)}
          >
            Delete
          </button>
        )}
      </div>
    );
  };

  // ------------------------------------------------------------------------
  return (
    <div className="page fwl-page">
      <PageHeader
        title="Framework Management"
        subtitle="Create and manage multiple assessment frameworks"
      />
      <HelpPanel help={FrameworkManagementHelp} />

      <div className="fwl-container">
        <div className="fwl-toolbar">
          <button type="button" className="btn btn-primary" onClick={openCreate}>
            + Create Framework
          </button>
          <span className="fwl-count">
            {frameworks.length} framework{frameworks.length === 1 ? '' : 's'}
          </span>
        </div>

        {loading ? (
          <div className="fwl-state">Loading frameworks…</div>
        ) : error ? (
          <div className="fwl-state fwl-state-error">
            <p>⚠️ {error}</p>
            <button type="button" className="btn btn-primary" onClick={loadFrameworks}>
              Retry
            </button>
          </div>
        ) : frameworks.length === 0 ? (
          <div className="fwl-state">
            <p>No frameworks yet — create one.</p>
            <button type="button" className="btn btn-primary" onClick={openCreate}>
              + Create Framework
            </button>
          </div>
        ) : (
          <div className="fwl-table-wrap">
            <table className="fwl-table">
              <thead>
                <tr>
                  <th>Framework</th>
                  <th>Status</th>
                  <th className="fwl-num">Dim</th>
                  <th className="fwl-num">Comp</th>
                  <th className="fwl-num">Q's</th>
                  <th className="fwl-completeness-col">Completeness</th>
                  <th className="fwl-actions-col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {frameworks.map((fw, idx) => {
                  const accent = idx === 0 || isLocked(fw);
                  return (
                    <tr key={fw.id} className={accent ? 'fwl-accent' : ''}>
                      <td>
                        <button
                          type="button"
                          className="fwl-name-link"
                          onClick={() => openBuilder(fw.id)}
                          title="Open in Builder"
                        >
                          {fw.name}
                        </button>
                        <div className="fwl-code">{fw.code}</div>
                        <div className="fwl-desc">{summaryLine(fw)}</div>
                        {/*
                          WHO A QUESTIONNAIRE AGAINST THIS FRAMEWORK WILL INVOLVE.

                          Placed under the summary rather than in a new column: the table's
                          columns are the source's, and the count of people is a property of
                          the framework's shape, which is what this block already describes.
                        */}
                        {fw.aspects.length > 0 && (
                          <div className="fwl-aspects" title="Who answers this framework">
                            {fw.aspects.map((a) => (
                              <span
                                key={a}
                                className="fwl-aspect-chip"
                                style={{ color: ASPECT_COLOR[a].fg, backgroundColor: ASPECT_COLOR[a].bg }}
                                title={`${ASPECT_OWNER[a]} — ${ASPECT_HINT[a]}`}
                              >
                                {a}
                              </span>
                            ))}
                            <span className="fwl-aspects-count">
                              {fw.aspects.length === 1
                                ? '1 owner per application'
                                : `${fw.aspects.length} owners per application`}
                            </span>
                          </div>
                        )}
                      </td>
                      <td>{renderStatusBadge(fw)}</td>
                      <td className="fwl-num fwl-stat">{fw.dimension_count}</td>
                      <td className="fwl-num fwl-stat">{fw.component_count}</td>
                      <td className="fwl-num fwl-stat">{fw.question_count}</td>
                      <td className="fwl-completeness-col">{renderCompleteness(fw)}</td>
                      <td>{renderActions(fw)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create / Edit modal */}
      {showForm && (
        <div
          className="form-modal"
          onClick={(e) => e.target === e.currentTarget && closeForm()}
        >
          <div className="form-modal-content">
            <div className="form-modal-header">
              <h2>{editing ? 'Edit Framework' : 'New Framework'}</h2>
              <button
                type="button"
                className="btn-close"
                onClick={closeForm}
                aria-label="Close dialog"
              >
                ✕
              </button>
            </div>
            <form className="customer-form" onSubmit={submitForm}>
              <div className="form-group">
                <label htmlFor="fwl-name">Name *</label>
                <input
                  id="fwl-name"
                  name="name"
                  type="text"
                  value={form.name}
                  onChange={handleFormChange}
                  required
                  placeholder="e.g., Enterprise Assessment Framework"
                />
              </div>
              <div className="form-group">
                <label htmlFor="fwl-code">Code *</label>
                <input
                  id="fwl-code"
                  name="code"
                  type="text"
                  value={form.code}
                  onChange={handleFormChange}
                  required
                  disabled={!!editing}
                  placeholder="e.g., ENT_STD"
                />
                {editing && (
                  <small className="fwl-hint">Code cannot be changed after creation.</small>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="fwl-description">Description</label>
                <textarea
                  id="fwl-description"
                  name="description"
                  rows={3}
                  value={form.description}
                  onChange={handleFormChange}
                  placeholder="Describe the scope and purpose of this framework"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="fwl-type">Type</label>
                  <select
                    id="fwl-type"
                    name="type"
                    value={form.type}
                    onChange={handleFormChange}
                  >
                    {FRAMEWORK_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="fwl-status">Status</label>
                  <select
                    id="fwl-status"
                    name="status"
                    value={form.status}
                    onChange={handleFormChange}
                  >
                    {FRAMEWORK_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={closeForm}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving…' : editing ? 'Save Changes' : 'Create Framework'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Clone modal */}
      {cloning && (
        <div
          className="form-modal"
          onClick={(e) => e.target === e.currentTarget && closeClone()}
        >
          <div className="form-modal-content">
            <div className="form-modal-header">
              <h2>Clone "{cloning.name}"</h2>
              <button
                type="button"
                className="btn-close"
                onClick={closeClone}
                aria-label="Close dialog"
              >
                ✕
              </button>
            </div>
            <form className="customer-form" onSubmit={submitClone}>
              <p className="fwl-modal-note">
                Creates a new framework by copying the dimensions, components and questions
                from <strong>{cloning.name}</strong>.
              </p>
              <div className="form-group">
                <label htmlFor="fwl-clone-name">Name *</label>
                <input
                  id="fwl-clone-name"
                  name="name"
                  type="text"
                  value={cloneForm.name}
                  onChange={handleCloneChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="fwl-clone-code">Code *</label>
                <input
                  id="fwl-clone-code"
                  name="code"
                  type="text"
                  value={cloneForm.code}
                  onChange={handleCloneChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="fwl-clone-description">Description</label>
                <textarea
                  id="fwl-clone-description"
                  name="description"
                  rows={3}
                  value={cloneForm.description}
                  onChange={handleCloneChange}
                />
              </div>
              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={closeClone}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Cloning…' : 'Clone Framework'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete confirm modal */}
      {deleting && (
        <div
          className="form-modal"
          onClick={(e) => e.target === e.currentTarget && !deletingBusy && closeDelete()}
        >
          <div className="form-modal-content fwl-delete-modal">
            <div className="form-modal-header">
              <h2>Delete Framework</h2>
              <button
                type="button"
                className="btn-close"
                onClick={closeDelete}
                aria-label="Close dialog"
                disabled={deletingBusy}
              >
                ✕
              </button>
            </div>
            <div className="customer-form">
              <p>
                Are you sure you want to delete <strong>{deleting.name}</strong> (
                {deleting.code})? This action cannot be undone.
              </p>
              {deleteError && <div className="fwl-delete-error">⚠️ {deleteError}</div>}
              {canForceDelete && (
                <div className="fwl-force-note">
                  This framework has assigned APR models. You can force-delete it, which will
                  unassign those models.
                </div>
              )}
              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={closeDelete}
                  disabled={deletingBusy}
                >
                  Cancel
                </button>
                {canForceDelete ? (
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={() => performDelete(true)}
                    disabled={deletingBusy}
                  >
                    {deletingBusy ? 'Deleting…' : 'Force Delete'}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={() => performDelete(false)}
                    disabled={deletingBusy}
                  >
                    {deletingBusy ? 'Deleting…' : 'Delete'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className={`fwl-toast fwl-toast-${toast.type}`} role="status">
          {toast.type === 'success' ? '✓ ' : '⚠ '}
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default FrameworksList;
